<template>
    <div class="departments">
        <ModeSwitcher
            ref="modeSwitcher"
            :main-mode-text="$t('create')"
            :create-mode-text="$t('create_department')"
            :edit-mode-text="$t('edit_department')"
            @reset="editDepartmentId = null"
        >
            <template v-slot:main-mode>
                <DepartmentFilters
                    v-model="filters"
                    :has-active-filters="hasActiveFilters"
                    @submit="loadData()"
                    @reset="resetFilters"
                />
                <v-data-table-server
                    :headers="useHeaders"
                    :items="useItems"
                    :items-per-page="-1"
                    :items-per-page-options="[-1]"
                    :loading="isLoading"
                    :items-length="useItems.length"
                    disable-sort
                    density="compact"
                    color="primary"
                    class="elevation-1 table-server"
                    data-cy="departments-table"
                >
                    <template v-slot:item.name="{item}">
                        <div class="text-link" :data-cy="`department-link-${item.id}`" @click="editDepartment(item.id)">
                            {{ item.name }}
                        </div>
                    </template>
                    <template v-slot:item.active="{item}">
                        <v-switch
                            v-model="item.active"
                            color="primary"
                            :label="item.active ? $t('active') : $t('inactive')"
                            hide-details
                            :data-cy="`department-status-${item.id}`"
                            @update:model-value="changeDepartmentStatus(item)"
                        />
                    </template>
                </v-data-table-server>
            </template>
            <template v-slot:additional-mode>
                <DepartmentForm
                    :department-id="editDepartmentId"
                    :company-id="companyId"
                    @save="save()"
                    @cancel="modeSwitcher.goToTable()"
                />
            </template>
        </ModeSwitcher>
    </div>
</template>

<script setup lang="ts">
    import DepartmentFilters from '~/components/account/departments/DepartmentFilters.vue'
    import type {DepartmentFiltersType} from '~/utils/types/account/departments/types'
    import DepartmentForm from '~/components/account/departments/DepartmentForm.vue'

    const initDepartmentFilters = (): DepartmentFiltersType => ({
        name: null,
        active: true,
    })

    type HeaderItemType = {
        title: string
        value: string
        width?: string
        minWidth?: string
    }

    const props = defineProps<{
        companyId: number
    }>()

    const isLoading = ref<boolean>(false)
    const departments = ref<any[]>([])
    const filters = ref<DepartmentFiltersType>(initDepartmentFilters())
    const modeSwitcher = ref(null)
    const editDepartmentId = ref<number>(null)

    const app = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {
                ...filters.value,
                ...(filters.value.name && {pattern: filters.value.name}),
            }
            delete rq.name

            const response = await app.$api.departments.get(rq)
            departments.value = response.departments
        } catch (e) {
            departments.value = []
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        await loadData()
        modeSwitcher.value.goToTable()
    }

    const useHeaders: ComputedRef<HeaderItemType[]> = computed(() => {
        const headers: HeaderItemType[] = [
            {title: t('name'), value: 'name'},
            {title: t('status'), value: 'active', width: '140px', minWidth: '140px'},
        ]
        return headers
    })

    const useItems = computed(() => {
        return departments.value.map(({name, active, id, legalClient}) => ({name, active, id, legalClient}))
    })

    const resetFilters = (): void => {
        filters.value = initDepartmentFilters()
        loadData()
    }

    const hasActiveFilters = computed(() => {
        return !isEqual(initDepartmentFilters(), filters.value)
    })

    const editDepartment = (id: number): void => {
        modeSwitcher.value.edit()
        editDepartmentId.value = id
    }

    const changeDepartmentStatus = async (item): Promise<void> => {
        const {id, active} = item
        try {
            isLoading.value = true
            await app.$api.departments.patch(id, {active})
            await loadData()
            app.$toast.success(t('success_message.saved_successfully'))
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
